import React, { useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, CircularProgress, Box, TextField,
    Stack,
} from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import ScannerNew from "../../../../components/common/ScannerNew";
import { GetEquipments } from "../../../services/GeneralMaintenanceService";
import { GetTodayReadingCount } from "../../../services/TemperatureTrackerService";

// "choose", "scan", "manual"
const STEP = { CHOOSE: "choose", SCAN: "scan", MANUAL: "manual" };

const TemperatureScannerDialog = ({ open, onClose }) => {
    const navigate = useNavigate();
    const [step, setStep] = useState(STEP.CHOOSE);
    const [manualText, setManualText] = useState("");
    const [looking, setLooking] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const reset = () => {
        setStep(STEP.CHOOSE);
        setManualText("");
        setLooking(false);
        setErrorMsg("");
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const lookupAndNavigate = async (description) => {
        setLooking(true);
        setErrorMsg("");
        try {
            const text = (description || "").trim();
            const equipResponse = await GetEquipments();
            const allEquipment = equipResponse?.results || [];
            const matched = allEquipment.find(
                (e) => (e.description || "").trim().toLowerCase() === text.toLowerCase()
            );

            if (!matched) {
                setErrorMsg(`No equipment found matching: "${text}"`);
                setLooking(false);
                return;
            }

            const thermometerCount = [matched.thermometer1, matched.thermometer2, matched.thermometer3]
                .filter((t) => t && t.trim()).length;

            const today = new Date().toISOString().split("T")[0];
            const countResponse = await GetTodayReadingCount((matched.description || "").trim(), today);
            const readingCount = countResponse?.results ?? 0;
            const readingNumber = readingCount === 0 ? "First" : "Second";

            reset();
            onClose();
            navigate("/quality-control/temperature-tracker/add", {
                state: {
                    fromScanner: true,
                    equipment: (matched.description || "").trim(),
                    equipmentType: matched.item || "",
                    readingNumber,
                    lowTemp: matched.lowTemp ?? null,
                    highTemp: matched.highTemp ?? null,
                    thermometerCount,
                },
            });
        } catch {
            setErrorMsg("Error looking up equipment. Please try again.");
            setLooking(false);
        }
    };

    const handleScan = (scannedText) => {
        if (looking) return;
        lookupAndNavigate(scannedText);
    };

    const handleManualSubmit = () => {
        if (!manualText.trim()) {
            setErrorMsg("Please enter the equipment description.");
            return;
        }
        lookupAndNavigate(manualText.trim());
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add Temperature Record</DialogTitle>

            <DialogContent>
                {/* Step 1 — choose method */}
                {step === STEP.CHOOSE && (
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            How would you like to identify the equipment?
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<QrCodeScannerIcon />}
                            onClick={() => { setErrorMsg(""); setStep(STEP.SCAN); }}
                            sx={{ justifyContent: "flex-start", py: 2 }}
                        >
                            Scan QR Code
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            startIcon={<EditIcon />}
                            onClick={() => { setErrorMsg(""); setStep(STEP.MANUAL); }}
                            sx={{ justifyContent: "flex-start", py: 2 }}
                        >
                            Enter Manually
                        </Button>
                    </Stack>
                )}

                {/* Step 2a — QR scanner */}
                {step === STEP.SCAN && (
                    looking ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
                            <CircularProgress size={24} />
                            <Typography>Looking up equipment...</Typography>
                        </Box>
                    ) : (
                        <ScannerNew onScan={handleScan} />
                    )
                )}

                {/* Step 2b — manual text entry */}
                {step === STEP.MANUAL && (
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Enter the equipment description exactly as it appears on the QR code label.
                        </Typography>
                        <TextField
                            label="Equipment Description"
                            value={manualText}
                            onChange={(e) => { setManualText(e.target.value); setErrorMsg(""); }}
                            onKeyDown={(e) => e.key === "Enter" && handleManualSubmit()}
                            fullWidth
                            size="small"
                            autoFocus
                            disabled={looking}
                        />
                        {looking && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <CircularProgress size={20} />
                                <Typography variant="body2">Looking up equipment...</Typography>
                            </Box>
                        )}
                    </Stack>
                )}

                {errorMsg && (
                    <Typography color="error" sx={{ mt: 2 }} variant="body2">
                        {errorMsg}
                    </Typography>
                )}
            </DialogContent>

            <DialogActions>
                {step !== STEP.CHOOSE && (
                    <Button onClick={() => { setErrorMsg(""); setStep(STEP.CHOOSE); }} disabled={looking}>
                        Back
                    </Button>
                )}
                {step === STEP.MANUAL && (
                    <Button
                        variant="contained"
                        onClick={handleManualSubmit}
                        disabled={looking || !manualText.trim()}
                    >
                        Look Up
                    </Button>
                )}
                <Button onClick={handleClose} disabled={looking} variant={step === STEP.CHOOSE ? "contained" : "outlined"} color={step === STEP.CHOOSE ? "inherit" : "secondary"}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TemperatureScannerDialog;
